import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ProceduresModal from '@/modules/configurationModule/detail/samples/procedures/ProceduresModal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import { IContentProceduresGrid, initialProceduresGridState } from '@/modules/configurationModule/detail/samples/procedures/Procedures.models'
import { proceduresConfigurationServices } from '@/api/resources/configuration/samples/procedures/ProceduresConfigurationServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ProceduresModal,
        CodificationSearcherModal
    }
})
export default class ProceduresGrid extends BaseCrudServerSideComponent<IContentProceduresGrid> implements BaseCrud {
    @Ref() readonly proceduresModalWrapper!: any
    @Ref() readonly codificationSearcherModalWrapper!: any

    constructor() {
        super()
        this.state = initialProceduresGridState()
    }

    showCodificationSearcherModal() {
        this.codificationSearcherModalWrapper.show()
    }

    showProcedureModal(title:string | any, selectedProcedure:IContentProceduresGrid) {
        this.state.actionTitle = title
        this.proceduresModalWrapper.show([selectedProcedure])
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentProceduresGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.proceduresModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentProceduresGrid) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(proceduresConfigurationServices.createProcedure(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.proceduresModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(data: IContentProceduresGrid[]) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(proceduresConfigurationServices.patchProceduresBatch(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.proceduresModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentProceduresGrid[]) {
        const proceduresToPatch = selectionBatch?.filter((procedure: IContentProceduresGrid) => procedure.active !== option)?.map((procedure:IContentProceduresGrid) => {
            return {
                ...procedure,
                active: option
            }
        })
        if (proceduresToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(proceduresConfigurationServices.patchProceduresBatch(proceduresToPatch))
            this.$_updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err)
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(topographies: IContentProceduresGrid[]) {
        const proceduresToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: topographies.map((procedure: IContentProceduresGrid) => Number(procedure?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(proceduresConfigurationServices.deleteProcedureBatch(proceduresToDelete))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (topographies.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: topographies?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(topographies: IContentProceduresGrid[]) {
        if (topographies.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(topographies)
            }
        }
    }
}
